// Bubble drawer chat — bottom-right tab opens a left-sliding drawer
// overlay. JSON contract is identical to chat-panel; the surface is
// just smaller and persists its conversation id in sessionStorage so
// the thread survives page navigations.
//
// DOM contract: drawer + backdrop toggle via the `.open` class on
// each element (CSS handles the transform + opacity transitions).
// Composer + messages reuse the SAME row builders as chat-panel from
// app/javascript/lib/chat_render.js — typography stays in lockstep.
//
// Source of truth for shape:
//   ~/Developer/work/autoboost/redesign/chat-design/chat-app.jsx
//   BubbleDrawer + chat-bubble-* CSS in Chat Redesign.html § 582-639.

import { Controller } from "@hotwired/stimulus"
import {
  buildMessageRow,
  buildToolLedger,
  buildThinkingRow,
  csrfToken,
} from "../lib/chat_render"

const STORAGE_KEY = "autoboost_chat_conversation_id"

export default class extends Controller {
  static targets = [
    "messages",      // scrollable conversation container
    "input",         // <textarea>
    "sendButton",    // <button>
    "sendLabel",     // <span> inside Send (toggles Send ↔ Stop)
    "hint",          // composer hint text
    "drawer",        // .chat-bubble-drawer
    "backdrop",      // .chat-bubble-backdrop
    "toggleButton",  // .chat-bubble-tab
  ]
  static values = {
    createUrl: String,
    showUrl: String,
    userId: String,           // current user id — for the thinking-row SSE target
  }

  // ---------- lifecycle ----------

  connect() {
    this.isOpen = false
    this.historyLoaded = false
    this.inflight = false
    this.conversationId = sessionStorage.getItem(STORAGE_KEY)
    console.log(`[chat-bubble] connect: createUrl=${this.createUrlValue} conversationId=${this.conversationId || "(none)"}`)
    // Send is disabled while empty; also flips on/off as the
    // transcribe controller streams partials into the textarea.
    this.updateSendEnabled()
  }

  toggle(event) {
    event?.preventDefault?.()
    this.isOpen ? this.close() : this.open()
  }

  async open() {
    this.isOpen = true
    console.log(`[chat-bubble] open conversationId=${this.conversationId || "(none)"} historyLoaded=${this.historyLoaded}`)

    this.backdropTarget.classList.add("open")
    this.drawerTarget.classList.add("open")
    if (this.hasToggleButtonTarget) this.toggleButtonTarget.style.display = "none"

    if (this.conversationId && !this.historyLoaded) {
      await this.loadHistory()
    } else if (!this.conversationId) {
      this.renderEmptyGreeting()
    }

    setTimeout(() => {
      if (this.hasInputTarget) this.inputTarget.focus()
    }, 250)
  }

  close() {
    this.isOpen = false
    console.log("[chat-bubble] close")
    this.backdropTarget.classList.remove("open")
    this.drawerTarget.classList.remove("open")
    if (this.hasToggleButtonTarget) {
      // CSS handles the slide-out animation; show the tab back once the
      // drawer is offscreen.
      setTimeout(() => { this.toggleButtonTarget.style.display = "" }, 280)
    }
  }

  closeOnBackdrop(event) {
    if (event.target === this.backdropTarget) this.close()
  }

  // ---------- history ----------

  async loadHistory() {
    if (!this.conversationId) return
    const url = `${this.showUrlValue}/${this.conversationId}`
    console.log(`[chat-bubble] loadHistory: GET ${url}`)
    try {
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken() },
        credentials: "same-origin",
      })
      console.log(`[chat-bubble] loadHistory response status=${response.status}`)
      if (!response.ok) {
        sessionStorage.removeItem(STORAGE_KEY)
        this.conversationId = null
        this.renderEmptyGreeting()
        return
      }
      const data = await response.json()
      console.log(`[chat-bubble] loadHistory: ${data.messages?.length || 0} message rows`)
      this.clearMessages()
      this.renderConversation(data.messages || [])
      this.historyLoaded = true
    } catch (e) {
      console.error("[chat-bubble] loadHistory error:", e)
    }
  }

  renderConversation(messages) {
    let pendingTools = []
    messages.forEach((msg) => {
      if (msg.role === "tool") {
        pendingTools.push(msg)
        return
      }
      if (pendingTools.length && msg.role === "assistant") {
        const ledger = buildToolLedger(pendingTools)
        if (ledger) this.messagesInner().appendChild(ledger)
        pendingTools = []
      }
      const row = buildMessageRow(msg)
      if (row) this.messagesInner().appendChild(row)
    })
    if (pendingTools.length) {
      const ledger = buildToolLedger(pendingTools)
      if (ledger) this.messagesInner().appendChild(ledger)
    }
    this.scrollToBottom()
  }

  // ---------- composer ----------

  send(event) {
    event?.preventDefault?.()
    if (this.inflight) {
      this.stop()
      return
    }
    this.sendCurrent()
  }

  enterPressed(event) {
    if (event.shiftKey) return
    event.preventDefault()
    if (!this.inflight) this.sendCurrent()
  }

  async sendCurrent() {
    // Notify any composer add-ons (transcribe controller, etc.) that a
    // send is about to fire so they can flush state into the textarea
    // BEFORE we read its value. Synchronous handlers only — no await.
    window.dispatchEvent(new CustomEvent("chat:before-send", { bubbles: true }))

    const content = this.inputTarget.value.trim()
    if (!content) return

    this.appendUserMessage(content)
    this.inputTarget.value = ""
    this.autoGrow()
    this.updateSendEnabled()
    this.beginInflight()

    try {
      console.log(`[chat-bubble] send: POST ${this.createUrlValue} chars=${content.length}`)
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          message: content,
          conversation_id: this.conversationId,
          source: "bubble",
        }),
      })
      console.log(`[chat-bubble] send response status=${response.status}`)
      const data = await response.json()

      if (!response.ok) {
        this.appendErrorMessage(data.error || "Unable to reach the assistant. Please retry.")
        return
      }
      if (data.conversation_id) {
        this.conversationId = String(data.conversation_id)
        sessionStorage.setItem(STORAGE_KEY, this.conversationId)
      }
      this.removeThinkingRow()
      this.appendAssistantRound(data.tool_messages || [], data.message)
    } catch (e) {
      console.error("[chat-bubble] send error:", e)
      this.removeThinkingRow()
      this.appendErrorMessage("Network error. Please retry.")
    } finally {
      this.endInflight()
      if (this.hasInputTarget) this.inputTarget.focus()
    }
  }

  stop() {
    console.log("[chat-bubble] stop pressed (UI-only)")
    this.removeThinkingRow()
    this.endInflight()
  }

  // Resets the conversation. New thread on next send.
  clear() {
    console.log("[chat-bubble] clear: new conversation")
    sessionStorage.removeItem(STORAGE_KEY)
    this.conversationId = null
    this.historyLoaded = false
    this.clearMessages()
    this.renderEmptyGreeting()
    if (this.hasInputTarget) this.inputTarget.focus()
  }

  // ---------- composer behaviors ----------

  // Send is disabled while empty and enabled once the textarea has
  // any non-whitespace content. Wired to the textarea's `input`
  // event in the composer partial so transcribe-streamed partials
  // also re-enable Send in real time.
  updateSendEnabled() {
    if (!this.hasSendButtonTarget || !this.hasInputTarget) return
    if (this.inflight) return
    const hasContent = this.inputTarget.value.trim().length > 0
    this.sendButtonTarget.disabled = !hasContent
  }

  autoGrow() {
    const ta = this.inputTarget
    if (!ta) return
    ta.style.height = "auto"
    ta.style.height = Math.min(160, ta.scrollHeight) + "px"
  }

  beginInflight() {
    this.inflight = true
    if (this.hasInputTarget) this.inputTarget.disabled = true
    if (this.hasSendLabelTarget) this.sendLabelTarget.textContent = "Stop"
    if (this.hasSendButtonTarget) this.sendButtonTarget.classList.add("stop")
    if (this.hasHintTarget) this.hintTarget.textContent = "Generating…"
    const thinking = buildThinkingRow("Thinking…")
    thinking.dataset.thinking = "1"
    if (this.hasUserIdValue && this.userIdValue) {
      thinking.id = `chat_thinking_user_${this.userIdValue}`
    }
    this.messagesInner().appendChild(thinking)
    this.scrollToBottom()
  }

  endInflight() {
    this.inflight = false
    if (this.hasInputTarget) this.inputTarget.disabled = false
    if (this.hasSendLabelTarget) this.sendLabelTarget.textContent = "Send"
    if (this.hasSendButtonTarget) this.sendButtonTarget.classList.remove("stop")
    if (this.hasHintTarget) this.hintTarget.innerHTML = "<kbd>↵</kbd> to send · <kbd>⇧↵</kbd> for newline"
    this.updateSendEnabled()
  }

  removeThinkingRow() {
    const inner = this.messagesInner()
    if (!inner) return
    inner.querySelectorAll('[data-thinking="1"]').forEach((el) => el.remove())
  }

  // ---------- rendering helpers ----------

  appendUserMessage(content) {
    const row = buildMessageRow({ role: "user", content })
    if (row) {
      const round = document.createElement("div")
      round.className = "chat-round"
      round.dataset.openRound = "1"
      round.appendChild(row)
      this.messagesInner().appendChild(round)
    }
    this.scrollToBottom()
  }

  appendAssistantRound(toolMessages, assistantPayload) {
    const inner = this.messagesInner()
    const round = inner.querySelector('[data-open-round="1"]')
    const ledger = toolMessages?.length ? buildToolLedger(toolMessages) : null
    const assistantRow = assistantPayload ? buildMessageRow(assistantPayload) : null
    if (round) {
      if (ledger) round.appendChild(ledger)
      if (assistantRow) round.appendChild(assistantRow)
      round.removeAttribute("data-open-round")
    } else {
      if (ledger) inner.appendChild(ledger)
      if (assistantRow) inner.appendChild(assistantRow)
    }
    this.scrollToBottom()
  }

  appendErrorMessage(text) {
    const row = buildMessageRow({ role: "system", content: text })
    if (row) this.messagesInner().appendChild(row)
    this.scrollToBottom()
  }

  // Greeting shown when the drawer opens with no active conversation.
  // Plain assistant turn — no tool ledger, no shape, no model meta.
  renderEmptyGreeting() {
    const inner = this.messagesInner()
    if (!inner || inner.childElementCount > 0) return
    const greeting = {
      role: "assistant",
      content: "Hi — ask me anything about your AutoBoost data.",
    }
    const row = buildMessageRow(greeting)
    if (row) inner.appendChild(row)
  }

  scrollToBottom() {
    if (!this.hasMessagesTarget) return
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  messagesInner() {
    if (!this.hasMessagesTarget) return null
    let inner = this.messagesTarget.querySelector(".chat-messages-inner")
    if (!inner) {
      inner = document.createElement("div")
      inner.className = "chat-messages-inner"
      this.messagesTarget.appendChild(inner)
    }
    return inner
  }

  clearMessages() {
    if (!this.hasMessagesTarget) return
    this.messagesTarget.innerHTML = '<div class="chat-messages-inner"></div>'
  }
}
